"use client";
import { PaymentEntityType } from "@/app/(procure)/procure/wallet/_components/pay-with-wallet-v2/usePayWithWallet";
import { getAmountBreakdown } from "@/app/(procure)/procure/wallet/services";
import { GetAmountBreakdownSchema } from "@/app/(procure)/procure/wallet/types";
import { Button } from "@/components/atoms";
import { initiatePayment, verifyPayment } from "@/lib/common/service";
import { PaymentMethods } from "@/lib/common/types";
import { usePaystackPayment } from "@/lib/helpers/hooks/usePaystack";
import { ArrowRightCircleIcon } from "@heroicons/react/20/solid";
import Image from "next/image";
import { useState } from "react";
import { toast } from "react-toastify";
import useSWR from "swr";
import { validateCartTotalAmount } from "../helpers/cartMinTotal";
import { getErrorMessage } from "@/lib";

export interface PayWithPaystack {
  label: string;
  paymentEntityType: PaymentEntityType;
  entityId: string;
  paymentKey: string;
  metadata: Record<string, any>;
  onSuccess: () => Promise<null>;
  onFailure: (error: any) => void;
  onInitiated?: () => void;
  onPending?: () => void;
  disabled?: boolean;
  onDisabledClick?: () => void;
}

export function PayWithPaystack(props: PayWithPaystack) {
  const { label, paymentKey } = props;
  const [isLoading, setLoading] = useState(false);

  const { data: amountBreakdown, isLoading: amountBreakdownIsLoading } =
    useSWR<GetAmountBreakdownSchema>(paymentKey, () =>
      getAmountBreakdown(props.paymentEntityType, props.entityId),
    );
  const isLoadingState = isLoading || amountBreakdownIsLoading;
  const isInteractionBlocked = Boolean(props.disabled);

  const handlePayStackSuccess = async (data: any) => {
    setLoading(true);
    const result = await verifyPayment({
      reference: data.reference.split("-")[1],
      provider: PaymentMethods.PAYSTACK,
    });

    if (result.ok === false) {
      props.onFailure("Payment verification failed");
    } else {
      toast.success("Payment verified successfully!");
      if (props.onSuccess) {
        props.onSuccess();
      }
    }
    setLoading(false);
  };

  const { handlePaystackPayment } = usePaystackPayment({
    handleSuccess: handlePayStackSuccess,
    label,
    amount: 0,
    onPaymentCancelled: () => {
      setLoading(false);
      props.onFailure("Payment was cancelled. You can safely try again.");
    },
  });

  const handlePaymentProcessing = async () => {
    if (props.disabled) {
      props.onDisabledClick?.();
      return;
    }

    setLoading(true);
    props.onInitiated?.();

    const paymentSession = await initiatePayment({
      amount: amountBreakdown?.amount ?? 0,
      paymentEntity: props.paymentEntityType,
      paymentEntityId: props.entityId,
      paymentKey: props.paymentKey,
      metadata: props.metadata,
      paymentProvider: PaymentMethods.PAYSTACK,
      description: "Paystack payment for cart with id: " + props.entityId,
    });

    if (paymentSession.ok === false) {
      props.onFailure(getErrorMessage(paymentSession));
      setLoading(false);
      return;
    }
    props.onPending?.();
    const reference = `DIRECT_PAYMENT-${paymentSession.id}`;

    await handlePaystackPayment(reference, {
      amount: amountBreakdown?.amount ?? 0,
      label: label,
    });
  };

  return (
    <Button
      full
      className="!bg-[#011b33] !h-11 text-sm px-4 md:!h-14 md:!text-sm md:!px-6 flex items-center justify-center gap-2 aria-disabled:!cursor-not-allowed aria-disabled:opacity-50"
      loading={isLoadingState}
      disabled={isLoadingState}
      aria-disabled={isInteractionBlocked}
      data-disabled={isInteractionBlocked ? "" : undefined}
      onClick={handlePaymentProcessing}
      aria-label="Pay with Paystack">
      Pay with
      <Image
        width={20}
        height={20}
        src="/images/paystacklogo.svg"
        alt="Paystack Logo"
        className="!size-12 md:!size-24"
      />
    </Button>
  );
}
